import { useEffect, useRef, useState } from "react";

// Under react-snap prerender, the IntersectionObserver never fires (no
// scroll, snapshot taken before animation) so the counter would freeze at
// 0 in the static HTML — search engines and social unfurls would see
// "0 million+" instead of "4 million+". Mirrors the GTM IIFE guard in
// public/index.html.
const IS_PRERENDER =
  typeof navigator !== "undefined" &&
  navigator.userAgent.indexOf("ReactSnap") !== -1;

function animateCount(targetVal, setCount) {
  const dur = 1500;
  const t0 = performance.now();
  const step = (now) => {
    const p = Math.min((now - t0) / dur, 1);
    const eased = 1 - Math.pow(1 - p, 4);
    setCount(Math.floor(eased * targetVal));
    if (p < 1) requestAnimationFrame(step);
    else setCount(targetVal);
  };
  requestAnimationFrame(step);
}

export default function AnimatedCounter({ target, suffix = "" }) {
  const [count, setCount] = useState(IS_PRERENDER ? target : 0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (IS_PRERENDER) return;
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          animateCount(target, setCount);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [target]);

  // suppressHydrationWarning: the react-snap snapshot holds the final value
  // (IS_PRERENDER above) while the first client render starts at 0. Without
  // it React 19 treats the text mismatch as a hydration failure (#418) and
  // regenerates the whole tree. The prerendered number stays visible until
  // the count-up animation starts.
  return (
    <span ref={ref} suppressHydrationWarning>
      {`${count.toLocaleString()}${suffix}`}
    </span>
  );
}
